import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Loader2, X, Search, ExternalLink, AlertTriangle, Info, ArrowUp, ArrowDown, ChevronDown,
  BarChart2, Grid3x3, Sparkles, Globe, MessageSquare, HelpCircle, BookOpen, CalendarDays, Repeat, Radar, ChevronRight, Layers, Smile,
} from 'lucide-react';
import { AI_VISIBILITY_BRAND_IDS, hasAiVisibility } from '../config/aiVisibilityBrands';
import AiVisibilityLanding from './AiVisibilityLanding';
import { brandLogo } from '../config/aiVisibilityLogos';
import AiBrandLogo from '../components/AiBrandLogo';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, LabelList } from 'recharts';

/**
 * AI Visibility — ChatGPT, Gemini ve Claude tüketici sorularında hangi markayı,
 * hangi ihtiyaçta, hangi kaynağa dayanarak öneriyor.
 * Backend: /api/ai-visibility (routes/aiVisibility.js), veri ai_visibility.* şeması.
 * Hangi projenin görüneceğini backend seçer (projects.owner_brand_ids):
 * dört Yataş markası AYNI Yataş Grup verisini, HUPALUPA Eğlence kendi projesini görür.
 * Route'lar: /ai-visibility (menü) ve /yatas/ai-visibility (müşteriye giden link).
 *
 * Görünüm bir DÖNEM (varsayılan: son ölçümün ayı) — dönemdeki tüm ölçümler tek
 * havuzda. İsteğe bağlı ikinci dönemle kıyas; oranların yanında %95 güven aralığı.
 */

const API = 'https://api.glowytics.com';


const PLATFORM_LABEL = { openai: 'ChatGPT', gemini: 'Gemini', claude: 'Claude', perplexity: 'Perplexity' };
const PLATFORM_ORDER = ['openai', 'gemini', 'claude', 'perplexity'];
const PLATFORM_LOGO = { openai: '/llm-chatgpt.svg', gemini: '/llm-gemini.svg', claude: '/llm-claude.svg', perplexity: '/llm-perplexity.svg' };

const PlatformLogo = ({ p, size = 'w-4 h-4' }) => (PLATFORM_LOGO[p]
  ? <img src={PLATFORM_LOGO[p]} alt={PLATFORM_LABEL[p] || p} className={`${size} shrink-0 object-contain`} />
  : null);

// Logo + ad; tablo başlıkları ve etiketler için
const PlatformName = ({ p, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 ${className}`}><PlatformLogo p={p} />{PLATFORM_LABEL[p] || p}</span>
);
const CATEGORY_LABEL = {
  uyku: 'Genel uyku', yatak: 'Yatak', baza: 'Baza & Başlık', yastik_yorgan: 'Yastık & Yorgan', koltuk: 'Koltuk',
  mobilya: 'Genel mobilya', yemek_odasi: 'Yemek Odası Takımı', yatak_odasi: 'Yatak Odası Takımı', ev_tekstili: 'Ev Tekstili',
  bahce: 'Bahçe Mobilyası', ev_dekoru: 'Ev Dekoru',
  // Europcar
  havalimani: 'Havalimanı', sehir: 'Şehir', senaryo: 'Senaryo', turist: 'Turist (İngilizce)',
  // HUPALUPA Eğlence
  genel: 'Genel', konum: 'Konum', dogum_gunu: 'Doğum Günü', aktivite: 'Aktivite', kucuk_yas: 'Küçük Yaş',
  egitim_atolye: 'Eğitim & Atölye', tatil: 'Tatil', grup: 'Okul & Grup',
  // Multinet
  isveren: 'İşveren', kullanici: 'Kart Kullanıcısı', isyeri: 'Üye İşyeri', yakit: 'Yakıt Kartı',
  hediye: 'Hediye Kartı', mevzuat: 'Mevzuat',
  // Derma kozmetik (derma-tr)
  akne: 'Akne ve sivilce', leke: 'Leke', hassas: 'Hassasiyet ve kızarıklık', kuru: 'Kuru cilt',
  gunes_yuz: 'Yüz güneş kremi', gunes_ozel: 'Çocuk, hassas ve spor güneş', derma_genel: 'Genel dermokozmetik',
  temizleyici: 'Temizleyici', nemlendirici: 'Nemlendirici', serum: 'Serum ve aktif içerik', yaslanma: 'Yaşlanma karşıtı',
  goz: 'Göz çevresi', bebek_cocuk: 'Bebek ve çocuk', hamile: 'Hamilelik ve emzirme', atopik: 'Atopik cilt ve egzama',
  erkek: 'Erkek bakımı', sac_dokulme: 'Saç dökülmesi', kepek: 'Kepek ve saç derisi', vucut: 'Vücut',
  ct_normal: 'Normal cilt', ct_kuru: 'Kuru cilt tipi', ct_yagli: 'Yağlı cilt', ct_karma: 'Karma cilt', ct_hassas: 'Hassas cilt',
  ct_akne: 'Akneye eğilimli cilt', ct_atopik: 'Atopik cilt tipi', ct_rozasea: 'Rozasealı cilt', ct_susuz: 'Susuz (dehidrate) cilt',
  niasinamid: 'Niasinamid', retinol: 'Retinol ve bakuchiol', peeling: 'AHA / BHA peeling', leke_aktif: 'Leke aktifleri',
  c_vitamini: 'C vitamini', hyaluronik_peptit: 'Hyaluronik asit, peptit, seramid', sivilce_bandi: 'Sivilce bandı ve kurutucu',
  maske_tonik: 'Maske, tonik, yağ bazlı temizleyici', k_beauty: 'Kore bakımı', dogal_temiz: 'Doğal ve vegan', uygun_aktif: 'Uygun fiyatlı aktifler',
  sedef: 'Sedef', madalyon: 'Madalyon hastalığı', kserozis: 'Kserozis (aşırı kuruluk)',
  // Kozmetik (kozmetik-tr, 4 Eki 2026)
  fondoten: 'Fondöten', kapatici: 'Kapatıcı', pudra: 'Pudra', allik_aydinlatici: 'Allık, bronzer ve aydınlatıcı',
  baz_sabitleyici: 'Makyaj bazı ve sabitleyici', maskara: 'Maskara', eyeliner: 'Eyeliner ve göz kalemi', far: 'Far paleti',
  kas: 'Kaş', ruj: 'Ruj ve dudak kalemi', dudak_parlatici: 'Dudak parlatıcısı, likit ruj ve dudak bakımı', misel_su: 'Misel su',
  goz_makyaj_temizleyici: 'Göz makyajı temizleyici (çift fazlı)', temizleme_yagi: 'Temizleme yağı ve balm',
  temizleme_mendil: 'Mendil, ped ve bez', luks_serum: 'Lüks serum', goz_kremi: 'Göz kremi', yaslanma_karsiti: 'Yaşlanma karşıtı',
  gece_bakimi: 'Gece bakımı', hediye_seti: 'Hediye seti', yuz_temizleyici: 'Yüz temizleyici', yuz_maskesi: 'Yüz maskesi',
  tonik: 'Tonik ve yüz spreyi', parfum_kadin: 'Kadın parfümü', parfum_erkek: 'Erkek parfümü',
  parfum_unisex: 'Unisex ve niş parfüm', parfum_mevsim: 'Mevsim ve imza koku', parfum_uygun: 'Uygun fiyatlı parfüm ve muadil',
  body_mist: 'Body mist ve saç parfümü', sampuan_bakim: 'Şampuan ve saç bakımı', sac_boyasi: 'Saç boyası',
  sekillendirici: 'Şekillendirici', sac_yagi_serum: 'Saç yağı ve serum', sac_cihazi: 'Saç cihazları', kbeauty: 'K-beauty',
  viral: 'Viral ve trend ürünler', temiz_vegan: 'Temiz ve vegan güzellik', oje: 'Oje', kalici_oje: 'Evde kalıcı / jel oje',
  tirnak_bakim: 'Tırnak bakımı ve takma tırnak', el_kremi: 'El kremi', vucut_losyon: 'Vücut losyonu ve yağı',
  deodorant: 'Deodorant', dus: 'Duş jeli ve sabun', epilasyon: 'Epilasyon, ağda ve tıraş', self_tan: 'Self-tan ve bronzlaştırıcı',
};
const TYPE_LABEL = {
  top_pick: 'İlk tercih', recommended: 'Önerildi', mentioned: 'Adı geçti', compared: 'Karşılaştırıldı', warned_against: 'Olumsuz',
};
const SOURCE_LABEL = {
  brand: 'Listedeki markaların siteleri', brand_other: 'Diğer marka siteleri', marketplace: 'Pazar yeri', retailer: 'Perakendeci',
  media: 'Blog / içerik sitesi', news: 'Haber sitesi', expert: 'Uzman / sağlık / kurum',
  review: 'Karşılaştırma / inceleme / şikayet', forum: 'Forum', video: 'Video', reference: 'Ansiklopedi', other: 'Diğer',
};
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const pct = (v, d = 0) => (v == null ? '–' : `%${(v * 100).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d })}`);
const pp = (v, d = 1) => (v == null ? '–' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v * 100).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d })} puan`);
const fmtRank = (v) => (v == null ? '–' : v.toLocaleString('tr-TR', { maximumFractionDigits: 1, minimumFractionDigits: 1 }));
const fmtDay = (s) => { if (!s) return ''; const [y, m, d] = s.split('-').map(Number); return `${d} ${MONTHS[m - 1]} ${y}`; };
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const iso = (d) => d.toISOString().slice(0, 10);
const monthRange = (y, m) => ({ from: `${y}-${String(m).padStart(2, '0')}-01`, to: iso(new Date(Date.UTC(y, m, 0))) });
const prevRange = ({ from, to }) => {
  // Aynı uzunlukta bir önceki dönem; tam ay seçiliyse bir önceki tam ay
  const [fy, fm, fd] = from.split('-').map(Number);
  const cur = monthRange(fy, fm);
  if (fd === 1 && to === cur.to) return fm === 1 ? monthRange(fy - 1, 12) : monthRange(fy, fm - 1);
  const [ty, tm, td] = to.split('-').map(Number);
  if (fd === 1 && ty === fy && tm === fm) {
    // Ayın başından bugüne → geçen ayın aynı günleri
    const prev = fm === 1 ? monthRange(fy - 1, 12) : monthRange(fy, fm - 1);
    const lastDay = Number(prev.to.slice(8));
    return { from: prev.from, to: `${prev.to.slice(0, 8)}${String(Math.min(td, lastDay)).padStart(2, '0')}` };
  }
  const a = new Date(`${from}T00:00:00Z`); const b = new Date(`${to}T00:00:00Z`);
  const len = Math.round((b - a) / 86400000) + 1;
  const pTo = new Date(a.getTime() - 86400000); const pFrom = new Date(pTo.getTime() - (len - 1) * 86400000);
  return { from: iso(pFrom), to: iso(pTo) };
};
const rangeLabel = (r) => {
  if (!r?.from) return '';
  const [fy, fm, fd] = r.from.split('-').map(Number);
  if (fd === 1 && r.to === monthRange(fy, fm).to) return `${MONTHS[fm - 1]} ${fy}`;
  return r.from === r.to ? fmtDay(r.from) : `${fmtDay(r.from)} – ${fmtDay(r.to)}`;
};

// Isı haritası: tek renk tonu, açıktan koyuya (sıralı ölçek)
function heatStyle(v) {
  if (v == null || v === 0) return { background: 'transparent', color: '#9ca3af' };
  const steps = ['#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb'];
  const i = Math.min(steps.length - 1, Math.floor(v * steps.length));
  return { background: steps[i], color: i >= 5 ? '#ffffff' : '#1f2937' };
}

// Panel kartı (Overview ile aynı dil: rounded-3xl, ikon rozetli başlık)
const ICON_TONE = {
  blue: 'bg-blue-50 text-blue-600', purple: 'bg-purple-50 text-purple-600', green: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600', rose: 'bg-rose-50 text-rose-600', gray: 'bg-gray-100 text-gray-600',
};
// Her kartın altındaki Glowytics imzası (16 Eki 2026, Efe)
function GlowyFoot({ inset = false }) {
  return (
    <div className={`flex items-center justify-end ${inset ? 'mt-5 pt-4 border-t border-black/[0.05]' : 'px-6 sm:px-10 py-4 border-t border-black/[0.05] bg-[#fbfbfd]'}`}>
      <img src="/glowytics-lockup-blue.png" alt="Glowytics" className="h-[18px] w-auto select-none opacity-90" draggable={false} />
    </div>
  );
}

function Card({ title, sub, children, right, icon: Icon, tone = 'blue', className = '', foot = true }) {
  return (
    <div className={`bg-white rounded-3xl border border-gray-200/80 shadow-sm p-6 ${className}`}>
      {(title || right) && (
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-start gap-3 min-w-0">
            {Icon && <div className={`p-2 rounded-xl shrink-0 ${ICON_TONE[tone]}`}><Icon className="w-5 h-5" /></div>}
            <div className="min-w-0">
              {title && <h2 className="text-lg font-semibold text-gray-800">{title}</h2>}
              {sub && <p className="text-sm text-gray-500 mt-0.5">{sub}</p>}
            </div>
          </div>
          {right}
        </div>
      )}
      {children}
      {foot && <GlowyFoot inset />}
    </div>
  );
}

const TH = 'py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap';

// Koşu bazında öneri payı (backend overview.trend). Dönem seçiminden bağımsız, tüm ölçümler;
// dünya/kategori/platform/soru seti filtreleri uygulanmış halde gelir.
const TREND_COLORS = ['#436AEB', '#E8833A', '#0E9F6E', '#9333EA'];
function ShareTrend({ trend }) {
  if (!trend?.points?.length) return null;
  const fmtDay = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : '');
  const rows = trend.points.map((p) => ({
    label: fmtDay(p.day), runId: p.runId, responses: p.responses, allRecs: p.allRecs,
    ...Object.fromEntries(trend.brands.map((b) => [`b${b.id}`, p.shares[b.id] == null ? null : +(p.shares[b.id] * 100).toFixed(1)])),
  }));
  const max = Math.max(5, ...rows.flatMap((r) => trend.brands.map((b) => r[`b${b.id}`] || 0)));
  const top = Math.ceil((max * 1.25) / 5) * 5;
  const TipBox = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const r = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-3 py-2 text-xs">
        <div className="font-semibold text-gray-800 mb-1">{r.label} · ölçüm #{r.runId}</div>
        {trend.brands.map((b, i) => (
          <div key={b.id} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: TREND_COLORS[i % TREND_COLORS.length] }} />
            <span className="text-gray-600">{b.name}</span>
            <span className="ml-auto font-semibold text-gray-900 tabular-nums">%{(r[`b${b.id}`] ?? 0).toLocaleString('tr-TR')}</span>
          </div>
        ))}
        <div className="text-gray-400 mt-1">{r.responses} cevap · {r.allRecs} marka önerisi</div>
      </div>
    );
  };
  return (
    <Card icon={BarChart2} title="Ölçümden ölçüme öneri payı"
      sub="Her nokta bir ölçüm. Yapay zekanın o ölçümde yaptığı her 100 marka önerisinden kaçının markaya gittiğini gösterir. Seçili dönemden bağımsızdır; segment, kategori, platform ve soru filtreleri uygulanır."
      right={(
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
          {trend.brands.map((b, i) => (
            <span key={b.id} className="inline-flex items-center gap-1.5">
              <span className="w-3 h-0.5 rounded" style={{ background: TREND_COLORS[i % TREND_COLORS.length] }} />{b.name}
            </span>
          ))}
        </div>
      )}>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 20, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
            <YAxis domain={[0, top]} tickFormatter={(v) => `%${v}`} tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} width={44} />
            <Tooltip content={<TipBox />} />
            {trend.brands.map((b, i) => (
              <Line key={b.id} type="monotone" dataKey={`b${b.id}`} name={b.name} stroke={TREND_COLORS[i % TREND_COLORS.length]}
                strokeWidth={2.5} dot={{ r: 4, strokeWidth: 0, fill: TREND_COLORS[i % TREND_COLORS.length] }} activeDot={{ r: 6 }} connectNulls isAnimationActive={false}>
                <LabelList dataKey={`b${b.id}`} position="top" formatter={(v) => (v == null ? '' : `%${v.toLocaleString('tr-TR')}`)}
                  style={{ fontSize: 11, fill: TREND_COLORS[i % TREND_COLORS.length], fontWeight: 600 }} />
              </Line>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {rows.length < 4 && <p className="text-xs text-gray-400 mt-2">Şimdilik {rows.length} ölçüm var; her Pazartesi ve Perşembe ölçümüyle bir nokta eklenir.</p>}
    </Card>
  );
}

function Seg({ value, onChange, options }) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto rounded-xl border border-gray-200/80 bg-white p-1 shadow-sm">
      {options.map((o) => (
        <button key={o.value ?? 'all'} onClick={() => onChange(o.value)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition ${value === o.value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          {o.logo && <span className={`inline-flex rounded ${value === o.value ? 'bg-white p-0.5' : ''}`}><PlatformLogo p={o.logo} size="w-3.5 h-3.5" /></span>}
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Güven aralığı: "%4,0–%10,2"
const CiText = ({ ci, d = 1 }) => (ci ? <span className="text-[11px] text-gray-400 whitespace-nowrap">{pct(ci[0], d)}–{pct(ci[1], d)}</span> : null);

// Dönem farkı: anlamlıysa renkli ok, değilse gri "değişim yok"
function Delta({ c, compact }) {
  if (!c) return null;
  if (!c.significant) {
    return <span className="text-[11px] text-gray-400 whitespace-nowrap" title={`Fark ${pp(c.diff)}; %95 aralık ${pp(c.ci?.[0])} / ${pp(c.ci?.[1])} — sıfırı içeriyor, doğal değişkenlik sınırında`}>
      {compact ? '≈' : `≈ ${pp(c.diff)}`}
    </span>;
  }
  const up = c.diff > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium whitespace-nowrap ${up ? 'text-emerald-700' : 'text-rose-700'}`}
      title={`%95 aralık ${pp(c.ci[0])} / ${pp(c.ci[1])}`}>
      {up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{pp(c.diff)}
    </span>
  );
}

// Tek bir marka kartı (kendi markalarımız)
function OwnBrandCard({ b, platforms, cmp }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-5 flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-gray-800 truncate">{b.name}</div>
        <span className="text-[11px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap">ort. sıra {fmtRank(b.avgRank)}</span>
      </div>
      <div className="mt-3 text-xs text-gray-500">Öneri payı (Share of Recommendation)</div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-900">{pct(b.share, 1)}</span>
        {cmp && <Delta c={cmp.share} />}
      </div>
      {b.ci && <div className="text-[11px] text-gray-400">%95 aralık <CiText ci={b.ci.share} /></div>}
      <div className="grid grid-cols-3 gap-2 mt-4 text-center">
        {[['Metinde geçme', b.mentionRate, 'mentionRate'], ['Önerilme', b.recommendationRate, 'recommendationRate'], ['İlk 3', b.top3Rate, 'top3Rate']].map(([l, v, k]) => (
          <div key={l} className="rounded-xl bg-gray-50 py-2">
            <div className="text-sm font-semibold text-gray-800">{pct(v)}</div>
            <div className="text-[11px] text-gray-500">{l}</div>
            {cmp && <div><Delta c={cmp[k]} compact /></div>}
          </div>
        ))}
      </div>
      {platforms.length > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2.5">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Platforma göre önerilme</div>
          {platforms.map((p) => {
            const r = b.byPlatform[p]?.recommendationRate;
            return (
              <div key={p} className="flex items-center gap-2 text-xs" title={`${PLATFORM_LABEL[p]}: önerilme ${pct(r)} · öneri payı ${pct(b.byPlatform[p]?.share, 1)}`}>
                <PlatformLogo p={p} />
                <span className="w-14 text-gray-600">{PLATFORM_LABEL[p]}</span>
                <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.min(100, (r || 0) * 100)}%` }} />
                </div>
                <span className="w-9 text-right font-medium text-gray-700">{pct(r)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Ham cevaplar paneli
function ResponsesDrawer({ open, onClose, title, loading, responses }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-full max-w-3xl h-full bg-gray-50 overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <div className="text-xs text-gray-500">Yapay zekanın verdiği cevaplar</div>
            <div className="font-semibold text-gray-800 break-words">{title}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {loading && <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="animate-spin" size={16} /> Yükleniyor…</div>}
          {!loading && responses.length === 0 && <div className="text-sm text-gray-500">Cevap yok.</div>}
          {responses.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-800 font-medium"><PlatformLogo p={r.platform} size="w-3.5 h-3.5" />{PLATFORM_LABEL[r.platform] || r.platform}</span>
                <span>{r.model_version}</span>
                <span>· {fmtDateTime(r.created_at)}</span>
                <span>· {r.search_used ? `web'de aradı (${(r.search_queries || []).length} sorgu)` : 'web araması yapmadı'}</span>
              </div>
              <div className="text-sm font-medium text-gray-800 mb-3">“{r.prompt_text}”</div>
              {!r.extracted && <div className="text-xs text-amber-700 mb-3">Bu cevap henüz ayrıştırılmadı; markalar birkaç dakika içinde görünecek.</div>}
              {r.extracted && r.mentions.length === 0 && <div className="text-xs text-gray-500 mb-3">Cevapta hiç marka geçmedi.</div>}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {r.mentions.map((m, i) => (
                  <span key={i} title={[TYPE_LABEL[m.type], ...(m.attributes || [])].join(' · ')}
                    className={`text-xs px-2 py-0.5 rounded-full border ${m.kind === 'own' ? 'border-blue-300 bg-blue-50 text-blue-800 font-medium' : m.known ? 'border-gray-300 bg-white text-gray-700' : 'border-dashed border-gray-300 text-gray-500'}`}>
                    {m.rank ? `${m.rank}. ` : ''}{m.name}{(m.products || []).length > 0 && <span className="text-gray-500"> — {m.products.slice(0, 2).join(', ')}{m.products.length > 2 ? ` +${m.products.length - 2}` : ''}</span>}{!m.rank ? ` (${TYPE_LABEL[m.type]?.toLowerCase()})` : ''}
                    {m.tone && <span className={`ml-1 ${TONE_TEXT[m.tone]}`}>· {TONE_LABEL[m.tone]}</span>}
                  </span>
                ))}
              </div>
              {r.mentions.filter((m) => m.kind === 'own' && m.tone).map((m, i) => (
                <div key={i} className={`mb-2 text-xs rounded-xl border px-3 py-2 ${TONE_CHIP[m.tone]}`}>
                  <b>{m.name}</b> · {TONE_LABEL[m.tone]}{m.toneQuote ? <>: “{m.toneQuote}”</> : null}
                  {m.cons?.length > 0 && <div className="mt-0.5">Çekince: {m.cons.join(' · ')}</div>}
                </div>
              ))}
              {(r.search_queries || []).length > 0 && (
                <div className="text-[11px] text-gray-400 mb-2">Arama sorguları: {r.search_queries.join(' · ')}</div>
              )}
              <details>
                <summary className="text-xs font-medium text-blue-600 cursor-pointer select-none">Cevabın tamamı</summary>
                <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-[480px] overflow-y-auto border-t border-gray-100 pt-2">{r.answer_text}</div>
              </details>
              {r.citations.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-gray-500 cursor-pointer select-none">Kaynaklar ({r.citations.length})</summary>
                  <ul className="mt-2 space-y-1">
                    {r.citations.map((c, i) => (
                      <li key={i} className="text-xs">
                        <a href={c.url} target="_blank" rel="noreferrer" className="text-gray-700 hover:text-blue-600 inline-flex items-start gap-1 break-all">
                          <ExternalLink size={10} className="mt-0.5 shrink-0" />
                          <span><span className="text-gray-400">{c.domain}</span> {c.title && c.title !== c.domain ? `— ${c.title}` : ''}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Dönem seçimi — Analytics gibi hazır aralıklar + özel aralık (Yataş: 24 Eyl)
const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function presetRanges() {
  const now = new Date();
  const day = (off) => { const d = new Date(now); d.setDate(d.getDate() + off); return localIso(d); };
  const dow = (now.getDay() + 6) % 7; // Pazartesi = 0
  const y = now.getFullYear(), m = now.getMonth() + 1;
  const lm = m === 1 ? monthRange(y - 1, 12) : monthRange(y, m - 1);
  return [
    // Ölçüm haftada iki gün (Pzt+Per, 25 Eyl) → Bugün/Dün çoğu gün boş kalırdı; kayan aralık her zaman bir koşu içerir
    { key: 'last7', label: 'Son 7 gün', from: day(-6), to: day(0) },
    { key: 'last30', label: 'Son 30 gün', from: day(-29), to: day(0) },
    { key: 'lastweek', label: 'Geçen hafta', from: day(-dow - 7), to: day(-dow - 1) },
    { key: 'thismonth', label: 'Bu ay', from: `${y}-${String(m).padStart(2, '0')}-01`, to: day(0) },
    { key: 'lastmonth', label: 'Geçen ay', ...lm },
  ];
}

function PeriodPicker({ value, onChange, label }) {
  const presets = presetRanges();
  const match = value?.from && presets.find((p) => p.from === value.from && p.to === value.to);
  const [custom, setCustom] = useState(!!value?.from && !match);
  useEffect(() => { if (value?.from && !match) setCustom(true); }, [value?.from, value?.to, match]);
  const cur = custom ? 'custom' : match ? match.key : '';
  const onSelect = (k) => {
    if (k === 'custom') { setCustom(true); if (!value?.from) onChange({ from: presets[3].from, to: presets[3].to }); return; }
    setCustom(false);
    const p = presets.find((x) => x.key === k);
    if (p) onChange({ from: p.from, to: p.to });
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {label && <span className="text-xs text-gray-500">{label}</span>}
      <select value={cur} onChange={(e) => onSelect(e.target.value)}
        className="text-sm border border-gray-200/80 rounded-xl pl-3 pr-8 py-2 bg-white shadow-sm font-medium text-gray-700">
        {!cur && <option value="">{value?.from ? rangeLabel(value) : 'Dönem seçin'}</option>}
        {presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        <option value="custom">Özel aralık…</option>
      </select>
      {custom && (
        <>
          <input type="date" value={value?.from || ''} max={value?.to || undefined} onChange={(e) => e.target.value && onChange({ from: e.target.value, to: value?.to || e.target.value })}
            className="text-sm border border-gray-200/80 rounded-xl px-2 py-1.5 bg-white shadow-sm" />
          <span className="text-gray-400 text-sm">–</span>
          <input type="date" value={value?.to || ''} min={value?.from || undefined} onChange={(e) => e.target.value && onChange({ from: value?.from || e.target.value, to: e.target.value })}
            className="text-sm border border-gray-200/80 rounded-xl px-2 py-1.5 bg-white shadow-sm" />
        </>
      )}
      {!custom && value?.from && <span className="text-xs text-gray-400">{rangeLabel(value)}</span>}
    </div>
  );
}

const platformNames = (data) => {
  const set = new Set();
  for (const r of data?.runs || []) for (const e of r.config?.platforms || []) set.add(e.platform);
  const names = PLATFORM_ORDER.filter((p) => set.has(p)).map((p) => PLATFORM_LABEL[p]);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} ve ${names[names.length - 1]}` : names[0] || '';
};

// Dünya görünümünde markalarımızın rakipler arasındaki yeri (öneri payı sıralaması)
function WorldRanking({ data, ownIds, className = '' }) {
  const rows = (data.brandTable || []).filter((b) => b.share != null);
  const top = rows.slice(0, 10);
  for (const id of ownIds) { const i = rows.findIndex((b) => b.id === id); if (i >= 10) top.push(rows[i]); }
  const max = Math.max(0.0001, ...rows.map((b) => b.share || 0));
  const worldLabel = (data.worlds || []).find((w) => w.key === data.world)?.label;
  return (
    <Card icon={BarChart2} className={className} title="Rakipler arasındaki yerimiz"
      sub={`${worldLabel || 'Bu segment'} rakip listesi, yapay zekanın önerilerinden aldığı paya göre sıralı.`}>
      <div className="space-y-2">
        {top.map((b) => {
          const me = ownIds.includes(b.id);
          return (
            <div key={b.id} className="flex items-center gap-3 text-sm">
              <span className="w-5 text-right text-xs text-gray-400">{rows.indexOf(b) + 1}</span>
              <span className={`w-28 truncate ${me ? 'font-semibold text-blue-700' : 'text-gray-700'}`}>{b.name}</span>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full rounded-full ${me ? 'bg-blue-600' : 'bg-gray-300'}`} style={{ width: `${((b.share || 0) / max) * 100}%` }} />
              </div>
              <span className={`w-12 text-right ${me ? 'font-semibold text-blue-700' : 'text-gray-600'}`}>{pct(b.share, 1)}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// Büyük markaların logosu (config/aiVisibilityLogos.js); yoksa null → çağıran adı yazar
const BrandLogo = ({ name, size = 18, maxW = 120 }) => <AiBrandLogo name={name} size={size} maxW={maxW} fallback={null} />;

// Marka → yapay zekanın o markadan adını verdiği ürünler (backend overview.productByBrand; dünya filtresine tabi)
function BrandProducts({ data, onOpen }) {
  const [all, setAll] = useState(false);
  const by = new Map((data.productByBrand || []).map((g) => [g.brandId, g]));
  if (!by.size) return null;
  const list = (data.brandTable || []).map((b) => ({ b, g: by.get(b.id) }));
  const shown = all ? list : list.slice(0, 12);
  const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
  return (
    <section className="bg-white rounded-[32px] ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_64px_-28px_rgba(0,0,0,0.18)] overflow-hidden">
      <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">Marka · ürün kırılımı</div>
        <h2 className="mt-1.5 text-[28px] sm:text-[34px] leading-[1.1] font-semibold tracking-[-0.03em] text-[#1d1d1f]">Markaların önerilen ürünleri.</h2>
        <p className="mt-3 text-[15px] leading-relaxed text-[#6e6e73] max-w-3xl">Yapay zeka bir markayı önerirken hangi ürünlerinin adını verdi. Yüzde, ürünün o markanın adı verilen ürün önerileri içindeki payı; sayı, kaç öneride geçtiği.</p>
      </div>
      <div className="px-6 sm:px-10 pb-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.map(({ b, g }, i) => {
          const max = Math.max(1, ...(g?.items || []).map((x) => x.recs));
          return (
            <div key={b.id} className={`rounded-[24px] p-5 sm:p-6 flex flex-col min-w-0 ${b.kind === 'own' ? 'bg-[#0071e3]/[0.05] ring-1 ring-[#0071e3]/20' : 'ring-1 ring-black/[0.06] bg-white'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">{i + 1}. sıra · %{((b.share || 0) * 100).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} pay</div>
                  <div className="mt-2 h-8 flex items-center">
                    <AiBrandLogo name={b.name} size={24} maxW={160} fallback={<span className="text-[20px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{b.name}</span>} />
                  </div>
                </div>
                {g && <div className="text-right shrink-0 text-[11px] leading-tight text-[#86868b] tabular-nums"><div><b className="text-[15px] font-semibold text-[#1d1d1f]">{nf(g.recs)}</b> ürün önerisi</div><div className="mt-0.5">{nf(g.productCount)} farklı ürün</div></div>}
              </div>
              {!g ? <div className="mt-5 text-[13px] text-[#86868b]">Ürün adı verilmeden önerildi.</div> : (
                <div className="mt-4 divide-y divide-black/[0.05]">
                  {g.items.slice(0, 6).map((x) => (
                    <button key={x.id} onClick={() => onOpen(`${b.name} · ${x.name}`, { productId: x.id })} className="w-full text-left py-2.5 group">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[14px] text-[#1d1d1f] min-w-0 truncate group-hover:text-[#0066cc]">{x.name}{x.level === 'seri' && <span className="ml-1.5 text-[11px] text-[#86868b]">seri</span>}</span>
                        <span className="shrink-0 text-[12px] text-[#86868b] tabular-nums"><b className="text-[13px] font-semibold text-[#1d1d1f]">{pct(x.share, 0)}</b> · {x.recs}×</span>
                      </div>
                      <div className="mt-1.5 h-1 rounded-full bg-black/[0.06] overflow-hidden">
                        <div className={`h-full rounded-full ${b.kind === 'own' ? 'bg-[#0071e3]' : 'bg-[#1d1d1f]/60'}`} style={{ width: `${(x.recs / max) * 100}%` }} />
                      </div>
                    </button>
                  ))}
                  {g.productCount > 6 && <div className="pt-2.5 text-[12px] text-[#86868b]">+ {g.productCount - 6} ürün daha</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {list.length > 12 && (
        <div className="cv-noprint px-6 sm:px-10 pb-8 -mt-2">
          <button onClick={() => setAll(!all)} className="text-[13px] font-medium text-[#0066cc] hover:underline inline-flex items-center gap-1">
            {all ? 'İlk 12 markayı göster' : `Tüm ${list.length} markayı göster`} <ChevronDown className={`w-3.5 h-3.5 transition ${all ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}
      <GlowyFoot />
    </section>
  );
}

// Marka karşılaştırmasının ürün hali: satır = AI'ın adını verdiği ürün/seri (yalnız ürün eşlemesi olan projelerde)
function ProductComparison({ data, platforms, platform, onOpen }) {
  const [brand, setBrand] = useState('');
  const [query, setQuery] = useState('');
  const [all, setAll] = useState(false);
  const allCats = data.productByCategory || [];
  const [view, setView] = useState('cat');
  const [catWorld, setCatWorld] = useState('');
  // Tüm kategoriler (backend dünyadan bağımsız döndürür); dünya tanımı varsa dünyaya göre gruplanır
  const worldDefs = data.worlds || [];
  const worldOf = (c) => worldDefs.find((w) => (w.categories || []).includes(c));
  const groups = useMemo(() => {
    const list = allCats.filter((c) => !catWorld || worldOf(c.category)?.key === catWorld);
    if (!worldDefs.length) return [{ key: '', label: null, cats: list }];
    const out = worldDefs.map((w) => ({ key: w.key, label: w.label, cats: list.filter((c) => worldOf(c.category)?.key === w.key) }));
    const rest = list.filter((c) => !worldOf(c.category));
    if (rest.length) out.push({ key: '_diger', label: 'Diğer', cats: rest });
    return out.filter((g) => g.cats.length);
  }, [allCats, catWorld, worldDefs]); // eslint-disable-line react-hooks/exhaustive-deps
  const cats = allCats;
  const rows = data.productTable || [];
  const brands = useMemo(() => {
    const m = new Map();
    for (const p of rows) m.set(p.brandId, { id: p.brandId, name: p.brand, n: (m.get(p.brandId)?.n || 0) + 1 });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [rows]);
  if (!rows.length) return null;
  const qn = query.trim().toLocaleLowerCase('tr');
  const filtered = rows.filter((p) => (!brand || p.brandId === Number(brand))
    && (!qn || `${p.brand} ${p.name}`.toLocaleLowerCase('tr').includes(qn)));
  const shown = all ? filtered : filtered.slice(0, 25);
  const maxShare = Math.max(0.0001, ...rows.map((p) => p.share || 0));
  const tot = data.productTotals;
  const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
  const Pills = ({ value, onChange, options }) => (
    <div className="inline-flex p-1 rounded-full bg-[#f5f5f7] gap-0.5">
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition ${value === o.value ? 'bg-white text-[#1d1d1f] shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'text-[#6e6e73] hover:text-[#1d1d1f]'}`}>{o.label}</button>
      ))}
    </div>
  );
  const Logo = ({ name, size, maxW, cls }) => <AiBrandLogo name={name} size={size} maxW={maxW} fallback={<span className={cls}>{name}</span>} />;
  const Tags = ({ x }) => (<>
    {x.level === 'seri' && <span className="ml-1.5 text-[11px] font-normal text-[#86868b]">seri</span>}
    {x.isDrug && <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 align-middle">ilaç</span>}
  </>);
  return (
    <section className="bg-white rounded-[32px] ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_64px_-28px_rgba(0,0,0,0.18)] overflow-hidden">
      {/* Başlık */}
      <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-6 items-end">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">Ürün karşılaştırması</div>
          <h2 className="mt-1.5 text-[28px] sm:text-[34px] leading-[1.1] font-semibold tracking-[-0.03em] text-[#1d1d1f]">Yapay zekanın önerdiği ürünler.</h2>
          <p className="mt-3 text-[15px] leading-relaxed text-[#6e6e73] max-w-3xl">Marka önerirken hangi ürünün ya da serinin adını verdi. Öneri payı, adı verilen her 100 ürün önerisinden kaçının o ürüne gittiğini gösterir. Bir ürüne tıklayınca geçtiği cevaplar açılır.</p>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[[cats.length, 'kategori'], [cats.reduce((n, c) => n + (c.prompts || 0), 0), 'soru'], [cats.reduce((n, c) => n + (c.productRecommendations || 0), 0), 'ürün önerisi']].map(([v, l]) => (
            <div key={l} className="rounded-2xl bg-[#f5f5f7] px-4 py-3 min-w-[92px]">
              <div className="text-[22px] sm:text-[26px] leading-none font-semibold tracking-[-0.02em] text-[#1d1d1f] tabular-nums">{nf(v)}</div>
              <div className="mt-1 text-[11px] text-[#86868b]">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Seçiciler */}
      <div className="px-6 sm:px-10 pb-6 flex flex-col items-start gap-3">
        {cats.length > 1 && <Pills value={view} onChange={setView} options={[{ value: 'cat', label: 'Kategoriye göre' }, { value: 'list', label: 'Tüm ürünler' }]} />}
        {cats.length > 1 && view === 'cat' && worldDefs.length > 1 && (
          <div className="self-stretch -mx-6 sm:-mx-10 px-6 sm:px-10 overflow-x-auto [scrollbar-width:none]">
            <div className="flex gap-1.5 w-max">
              {[{ key: '', label: 'Tümü', n: allCats.length }, ...worldDefs.map((w) => ({ key: w.key, label: w.label, n: allCats.filter((c) => (w.categories || []).includes(c.category)).length }))].map((w) => (
                <button key={w.key || 'all'} onClick={() => setCatWorld(w.key)}
                  className={`whitespace-nowrap text-[13px] px-3.5 py-1.5 rounded-full transition ${catWorld === w.key ? 'bg-[#1d1d1f] text-white' : 'bg-[#f5f5f7] text-[#424245] hover:bg-[#e8e8ed]'}`}>
                  {w.label} <span className={catWorld === w.key ? 'text-white/60' : 'text-[#86868b]'}>{w.n}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {cats.length > 1 && view === 'cat' ? (
        <div className="px-6 sm:px-10 pb-10 space-y-10">
          {groups.map((g) => (
            <div key={g.key || 'all'}>
              {g.label && (
                <div className="flex items-baseline gap-2 mb-4">
                  <h3 className="text-[21px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{g.label}</h3>
                  <span className="text-[13px] text-[#86868b]">{g.cats.length} kategori</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
                {g.cats.map((c) => {
                  const items = c.items.slice(0, 10);
                  const [lead, ...rest] = items;
                  const max = Math.max(0.0001, ...items.map((x) => x.share || 0));
                  const label = CATEGORY_LABEL[c.category] || c.category;
                  const open = (x) => onOpen(`${x.brand} · ${x.name} · ${label}`, { productId: x.id, category: c.category });
                  return (
                    <div key={c.category} className="rounded-[24px] ring-1 ring-black/[0.06] bg-white p-5 sm:p-6 flex flex-col min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="text-[17px] font-semibold tracking-[-0.01em] text-[#1d1d1f] min-w-0">{label}</div>
                        <div className="text-[11px] text-[#86868b] whitespace-nowrap tabular-nums">{c.prompts} soru · {nf(c.productRecommendations)} öneri</div>
                      </div>
                      {!lead && <div className="mt-6 text-[13px] text-[#86868b]">Bu kategoride ürün adıyla öneri yok.</div>}
                      {lead && (
                        <button onClick={() => open(lead)} className="mt-4 -mx-2 px-3 py-3 rounded-2xl bg-[#f5f5f7] hover:bg-[#ededf0] transition text-left flex items-center gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center min-h-[28px]"><Logo name={lead.brand} size={24} maxW={150} cls="text-[13px] font-semibold text-[#424245]" /></div>
                            <div className="mt-1 text-[15px] leading-snug font-semibold text-[#1d1d1f] break-words">{lead.name}<Tags x={lead} /></div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[28px] leading-none font-semibold tracking-[-0.03em] text-[#1d1d1f] tabular-nums">{pct(lead.share, 1)}</div>
                            <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[#86868b]">1. sıra</div>
                          </div>
                        </button>
                      )}
                      {rest.length > 0 && (
                        <div className="mt-2 divide-y divide-black/[0.05]">
                          {rest.map((x, i) => (
                            <button key={x.id} onClick={() => open(x)} className="w-full flex items-center gap-3 text-left py-2.5 group">
                              <span className="w-4 text-right text-[12px] text-[#86868b] tabular-nums shrink-0">{i + 2}</span>
                              <span className="flex-1 min-w-0">
                                <span className="flex items-center min-h-[18px]"><Logo name={x.brand} size={15} maxW={96} cls="text-[11px] font-medium text-[#6e6e73]" /></span>
                                <span className="block text-[14px] text-[#1d1d1f] truncate group-hover:text-[#0066cc]">{x.name}<Tags x={x} /></span>
                              </span>
                              <span className="w-16 sm:w-20 h-1 rounded-full bg-black/[0.06] overflow-hidden shrink-0">
                                <span className="block h-full rounded-full bg-[#1d1d1f]/60" style={{ width: `${((x.share || 0) / max) * 100}%` }} />
                              </span>
                              <span className="w-11 text-right text-[13px] font-semibold text-[#1d1d1f] tabular-nums shrink-0">{pct(x.share, 1)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <p className="text-[12px] leading-relaxed text-[#86868b] max-w-4xl">Pay, o kategorideki sorulara verilen cevaplarda adı verilen her 100 ürün önerisinden kaçının o ürüne gittiğini gösterir. Soru sayısı az olan kategorilerde tek bir cevap sıralamayı değiştirebilir.{tot && tot.unmatchedRecommendations > 0 && <> Dönemdeki {nf(tot.productRecommendations)} ürün önerisinin {nf(tot.unmatchedRecommendations)} tanesi henüz kataloğumuzdaki bir ürünle eşleşmedi; payda onları da sayar.</>}</p>
        </div>
      ) : (
        <div className="px-6 sm:px-10 pb-10">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#86868b] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ürün ara"
                className="pl-8 pr-3 py-2 text-[13px] rounded-full bg-[#f5f5f7] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30 w-60" />
            </div>
            <select value={brand} onChange={(e) => setBrand(e.target.value)}
              className="py-2 pl-3.5 pr-8 text-[13px] rounded-full bg-[#f5f5f7] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/30">
              <option value="">Tüm markalar</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.n})</option>)}
            </select>
            <span className="text-[12px] text-[#86868b]">{filtered.length} ürün</span>
          </div>
          <div className="overflow-x-auto rounded-[20px] ring-1 ring-black/[0.06]">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#86868b] border-b border-black/[0.06]">
                  <th className="py-3 px-4 min-w-[240px] font-semibold">Ürün</th>
                  <th className="py-3 px-4 min-w-[200px] font-semibold">Öneri payı</th>
                  <th className="py-3 px-4 text-right font-semibold whitespace-nowrap">Metinde geçme</th>
                  <th className="py-3 px-4 text-right font-semibold">Önerilme</th>
                  <th className="py-3 px-4 text-right font-semibold whitespace-nowrap">İlk 3</th>
                  <th className="py-3 px-4 text-right font-semibold whitespace-nowrap">Ort. sıra</th>
                  {!platform && platforms.map((p) => (
                    <th key={p} className="py-3 px-4 text-right font-semibold normal-case tracking-normal text-[12px]" title={`${PLATFORM_LABEL[p]}'ta önerilme oranı`}>
                      <span className="inline-flex items-center gap-1.5 justify-end whitespace-nowrap"><PlatformLogo p={p} size="w-3.5 h-3.5" />{PLATFORM_LABEL[p]}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-black/[0.05]">
                {shown.map((p) => (
                  <tr key={p.id} onClick={() => onOpen(`${p.brand} · ${p.name}`, { productId: p.id })}
                    className={`group cursor-pointer transition ${p.kind === 'own' ? 'bg-[#0071e3]/[0.04] hover:bg-[#0071e3]/[0.08]' : 'hover:bg-[#f5f5f7]'}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center min-h-[18px] mb-0.5"><Logo name={p.brand} size={16} maxW={110} cls="text-[11px] font-medium text-[#6e6e73]" /></div>
                      <div className={`text-[14px] font-medium ${p.kind === 'own' ? 'text-[#0066cc]' : 'text-[#1d1d1f]'}`}>{p.name}<Tags x={p} /></div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
                          <div className={`h-full rounded-full ${p.kind === 'own' ? 'bg-[#0071e3]' : 'bg-[#1d1d1f]/60'}`} style={{ width: `${((p.share || 0) / maxShare) * 100}%` }} />
                        </div>
                        <div className="w-14 text-right text-[14px] font-semibold text-[#1d1d1f] tabular-nums">{pct(p.share, 1)}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right text-[#424245] tabular-nums">{pct(p.mentionRate, 1)}</td>
                    <td className="py-3 px-4 text-right text-[#424245] tabular-nums">{pct(p.recommendationRate, 1)}</td>
                    <td className="py-3 px-4 text-right text-[#424245] tabular-nums">{pct(p.top3Rate, 1)}</td>
                    <td className="py-3 px-4 text-right text-[#424245] tabular-nums">{fmtRank(p.avgRank)}</td>
                    {!platform && platforms.map((pl) => <td key={pl} className="py-3 px-4 text-right text-[#6e6e73] tabular-nums">{pct(p.byPlatform[pl]?.recommendationRate ?? (data.totals.platforms.includes(pl) ? 0 : null), 1)}</td>)}
                  </tr>
                ))}
                {!shown.length && <tr><td colSpan={6 + (platform ? 0 : platforms.length)} className="py-8 text-center text-[13px] text-[#86868b]">Eşleşen ürün yok.</td></tr>}
              </tbody>
            </table>
          </div>
          {filtered.length > 25 && (
            <button onClick={() => setAll(!all)} className="mt-4 text-[13px] font-medium text-[#0066cc] hover:underline inline-flex items-center gap-1">
              {all ? 'İlk 25 ürünü göster' : `Tüm ${filtered.length} ürünü göster`} <ChevronDown className={`w-3.5 h-3.5 transition ${all ? 'rotate-180' : ''}`} />
            </button>
          )}
          <p className="mt-5 text-[12px] leading-relaxed text-[#86868b] max-w-4xl">Oranlar marka tablosuyla aynı paydada: cevapların yüzde kaçında ürünün adı geçti, yüzde kaçında önerildi, yüzde kaçında ilk üçteydi. <b className="font-semibold text-[#6e6e73]">Ort. sıra</b> ürünün markasının öneri listesindeki sırası. "Seri" satırları, yapay zekanın tek ürün yerine yalnız seri adını verdiği önerilerdir. Markayı ürün adı vermeden önerdiği cevaplar bu tabloya girmez.{tot && tot.unmatchedRecommendations > 0 && <> Bu dönemdeki {nf(tot.productRecommendations)} ürün önerisinin {nf(tot.unmatchedRecommendations)} tanesi henüz kataloğumuzdaki bir ürünle eşleşmedi.</>}</p>
        </div>
      )}
      <GlowyFoot />
    </section>
  );
}

const SENT_LABEL = { 1: 'olumlu', 0: 'nötr', '-1': 'olumsuz' };
const TONE_LABEL = { positive: 'olumlu', mixed: 'karışık', neutral: 'nötr', negative: 'olumsuz' };
const TONE_ORDER = ['positive', 'mixed', 'neutral', 'negative'];
const TONE_BAR = { positive: 'bg-emerald-500', mixed: 'bg-amber-400', neutral: 'bg-gray-300', negative: 'bg-rose-500' };
const TONE_TEXT = { positive: 'text-emerald-700', mixed: 'text-amber-700', neutral: 'text-gray-600', negative: 'text-rose-700' };
const TONE_CHIP = { positive: 'bg-emerald-50 text-emerald-700 border-emerald-200', mixed: 'bg-amber-50 text-amber-800 border-amber-200', neutral: 'bg-gray-50 text-gray-600 border-gray-200', negative: 'bg-rose-50 text-rose-700 border-rose-200' };

function ToneBar({ t, className = 'h-2' }) {
  return (
    <div className={`flex rounded-full overflow-hidden bg-gray-100 ${className}`}>
      {TONE_ORDER.map((k) => (t[k] ? <div key={k} className={TONE_BAR[k]} style={{ width: `${(t[k] / t.n) * 100}%` }} title={`${TONE_LABEL[k]}: ${t[k]}`} /> : null))}
    </div>
  );
}

// Ton: AI markadan nasıl bahsediyor — markasız sorulardaki her bahis olumlu / karışık / nötr / olumsuz
function ToneCard({ data, own, onOpen }) {
  const tb = data.toneByBrand || {};
  const rows = data.brandTable.filter((b) => tb[b.id]?.n);
  const [focus, setFocus] = useState(null);
  const focusId = focus && tb[focus] ? focus : own.find((b) => tb[b.id])?.id;
  const f = focusId ? tb[focusId] : null;
  const fName = data.brandTable.find((b) => b.id === focusId)?.name;
  const [showAllQ, setShowAllQ] = useState(false);
  if (!rows.length) {
    return (
      <Card icon={Smile} tone="green" title="Yapay zeka markamızdan nasıl bahsediyor">
        <div className="text-sm text-gray-500">Seçili dönemde ton ölçümü yok. Ton, 2 Ekim'den itibaren her ölçümde çıkarılıyor.</div>
      </Card>
    );
  }
  const critRate = (t) => (t.n ? (t.mixed + t.negative) / t.n : 0);
  return (
    <Card icon={Smile} tone="green" title="Yapay zeka markamızdan nasıl bahsediyor"
      sub="Markanın adı geçen her cevapta yapay zekanın markadan nasıl söz ettiği: olumlu (öneriyor, övüyor), karışık (artı ile birlikte bir çekince de söylüyor, ör. “kaliteli ama pahalı”), nötr (yalnızca adı geçiyor) ya da olumsuz. Satıra tıklayınca o markanın çekinceleri ve cümleleri aşağıda açılır.">
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/60 text-left">
              <th className={TH}>Marka</th>
              <th className={`${TH} w-72`}>Ton dağılımı</th>
              {TONE_ORDER.map((k) => <th key={k} className={`${TH} text-right`}>{TONE_LABEL[k]}</th>)}
              <th className={`${TH} text-right`} title="Karışık + olumsuz bahislerin payı">Çekinceli</th>
              <th className={`${TH} text-right`}>Bahis</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((b) => {
              const t = tb[b.id];
              const sel = b.id === focusId;
              return (
                <tr key={b.id} onClick={() => { setFocus(b.id); setShowAllQ(false); }}
                  className={`cursor-pointer transition ${sel ? 'bg-emerald-50/60' : b.kind === 'own' ? 'bg-blue-50/40 hover:bg-blue-50/80' : 'bg-white hover:bg-gray-50/60'}`}>
                  <td className={`py-2.5 px-4 font-medium whitespace-nowrap ${b.kind === 'own' ? 'text-blue-700' : 'text-gray-800'}`}>{b.name}</td>
                  <td className="py-2.5 px-4"><ToneBar t={t} /></td>
                  {TONE_ORDER.map((k) => <td key={k} className={`py-2.5 px-4 text-right ${t[k] ? TONE_TEXT[k] : 'text-gray-300'}`}>{pct(t[k] / t.n)}</td>)}
                  <td className="py-2.5 px-4 text-right font-semibold text-gray-800">{pct(critRate(t))}</td>
                  <td className="py-2.5 px-4 text-right text-gray-500">{t.n}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {own.some((b) => tb[b.id]?.quotes?.length) && (
        <div className="mt-6">
          <div className="text-sm font-semibold text-gray-800 mb-1">Olumsuz tarafta kurduğu cümleler: örnekler</div>
          <p className="text-xs text-gray-500 mb-3">Yapay zekanın markalarımız için çekince ya da eleştiri söylediği cümleler, cevaptan birebir. Önce olumsuz, sonra karışık bahisler. Cümleye tıklayınca cevabın tamamı açılır.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {own.filter((b) => tb[b.id]?.quotes?.length).map((b) => (
              <div key={b.id} className="rounded-2xl border border-rose-100 bg-rose-50/30 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-blue-700">{b.name}</span>
                  <button onClick={() => { setFocus(b.id); setShowAllQ(true); }} className="text-[11px] font-medium text-blue-600 hover:underline">
                    Tümü ({tb[b.id].quotes.length}) ›
                  </button>
                </div>
                <div className="space-y-2">
                  {tb[b.id].quotes.slice(0, 3).map((x, i) => (
                    <button key={i} onClick={() => onOpen(x.prompt, { promptId: x.promptId, platform: x.platform })}
                      className="w-full text-left rounded-xl bg-white border border-gray-200/80 px-3 py-2 hover:border-blue-200 transition">
                      <div className="text-sm text-gray-800">“{x.quote}”</div>
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-500 min-w-0">
                        <PlatformLogo p={x.platform} size="w-3.5 h-3.5" />
                        <span className={`px-1.5 py-0.5 rounded-full border shrink-0 ${TONE_CHIP[x.tone]}`}>{TONE_LABEL[x.tone]}</span>
                        <span className="truncate">Soru: {x.prompt}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {f && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <div className="text-sm font-semibold text-gray-800 mb-2">{fName}: dile getirilen çekinceler</div>
              <div className="flex flex-wrap gap-1.5">
                {f.cons.map((c) => (
                  <span key={c.text} className="inline-flex items-center gap-1.5 text-xs pl-2.5 pr-1 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-800">
                    {c.text}<b className="px-1.5 rounded-full bg-white text-rose-700 text-[11px]">{c.n}</b>
                  </span>
                ))}
                {!f.cons.length && <span className="text-xs text-gray-400">Bu dönemde çekince söylenmedi.</span>}
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-800 mb-2">{fName}: öne çıkarılan artılar</div>
              <div className="flex flex-wrap gap-1.5">
                {f.pros.map((c) => (
                  <span key={c.text} className="inline-flex items-center gap-1.5 text-xs pl-2.5 pr-1 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
                    {c.text}<b className="px-1.5 rounded-full bg-white text-emerald-700 text-[11px]">{c.n}</b>
                  </span>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-gray-400">Sayı: o ifadenin kaç cevapta geçtiği. İfadeler cevaptan kısaltılarak alınır; benzer anlamdaki ifadeler ayrı sayılabilir.</p>
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800 mb-2">Çekinceli ve olumsuz cümleler <span className="text-gray-400 font-normal">({f.quotes.length})</span></div>
            {!f.quotes.length && <div className="text-xs text-gray-400">Yok.</div>}
            <div className="space-y-2">
              {f.quotes.slice(0, showAllQ ? undefined : 5).map((x, i) => (
                <button key={i} onClick={() => onOpen(x.prompt, { promptId: x.promptId, platform: x.platform })}
                  className="w-full text-left rounded-2xl border border-gray-200/80 p-3 hover:border-blue-200 hover:bg-blue-50/20 transition">
                  <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-1">
                    <PlatformLogo p={x.platform} size="w-3.5 h-3.5" />
                    <span className={`px-1.5 py-0.5 rounded-full border ${TONE_CHIP[x.tone]}`}>{TONE_LABEL[x.tone]}</span>
                    <span className="truncate">“{x.prompt}”</span>
                  </div>
                  <div className="text-sm text-gray-800">{x.quote}</div>
                  {x.cons?.length > 0 && <div className="mt-1 text-[11px] text-rose-700">Çekince: {x.cons.join(' · ')}</div>}
                </button>
              ))}
            </div>
            {f.quotes.length > 5 && (
              <button onClick={() => setShowAllQ((v) => !v)} className="mt-3 text-xs font-medium text-blue-600 hover:underline">
                {showAllQ ? 'Daha az göster' : `Tümünü göster (${f.quotes.length})`}
              </button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// Marka soruları: soruda markanın adı geçiyor → öneri payına girmez; AI marka hakkında ne diyor
function BrandedQuestions({ data, onOpen }) {
  const list = data.brandedQuestions || [];
  return (
    <Card icon={MessageSquare} tone="purple" title="Markamız doğrudan sorulduğunda"
      sub={'Tüketicinin markamızın adını vererek sorduğu sorular ("Enza Home kaliteli mi?", "Yataş mı İstikbal mi?"). Markanın adı soruda zaten geçtiği için bu sorular öneri payına katılmaz. Burada yapay zekanın markamızdan olumlu mu, çekinceli mi, olumsuz mu bahsettiği ve yerine hangi markaları gösterdiği görülüyor. Karta tıklayınca cevapların tamamı açılır.'}>
      {list.length === 0 && <div className="text-sm text-gray-500">Seçili dönemde bu sorulara verilmiş cevap yok.</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {list.map((q) => {
          const tot = q.sentiment.pos + q.sentiment.neu + q.sentiment.neg + (q.sentiment.mix || 0);
          const w = (n) => `${tot ? (n / tot) * 100 : 0}%`;
          return (
            <button key={q.id} onClick={() => onOpen(q.text, { promptId: q.id })}
              className="text-left rounded-2xl border border-gray-200/80 p-4 hover:border-blue-200 hover:bg-blue-50/20 transition">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold text-blue-700">{q.brand}</div>
                  <div className="text-sm font-medium text-gray-800">“{q.text}”</div>
                </div>
                <span className="text-[11px] text-gray-400 whitespace-nowrap">{q.responses} cevap</span>
              </div>
              <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-gray-100">
                <div className="bg-emerald-500" style={{ width: w(q.sentiment.pos) }} />
                <div className="bg-amber-400" style={{ width: w(q.sentiment.mix || 0) }} />
                <div className="bg-gray-300" style={{ width: w(q.sentiment.neu) }} />
                <div className="bg-rose-500" style={{ width: w(q.sentiment.neg) }} />
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px] text-gray-500">
                <span><b className="text-emerald-700">{q.sentiment.pos}</b> olumlu</span>
                {q.sentiment.mix > 0 && <span><b className="text-amber-700">{q.sentiment.mix}</b> karışık</span>}
                <span><b className="text-gray-700">{q.sentiment.neu}</b> nötr</span>
                <span><b className="text-rose-700">{q.sentiment.neg}</b> olumsuz</span>
                <span>· {q.recommended}/{q.responses} cevapta önerildi</span>
              </div>
              {(q.cons || []).length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-gray-400">Çekince:</span>
                  {q.cons.map((c) => <span key={c.text} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-800">{c.text} <b>{c.n}</b></span>)}
                </div>
              )}
              {q.others.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-gray-400">Yanında önerilen / kıyaslanan:</span>
                  {q.others.map((o) => (
                    <span key={o.name} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200/80 text-gray-700">{o.name} <b>{o.n}</b></span>
                  ))}
                </div>
              )}
              <div className="mt-3 space-y-1.5">
                {PLATFORM_ORDER.filter((p) => q.latest[p]).map((p) => (
                  <div key={p} className="flex items-start gap-2 text-xs text-gray-600">
                    <PlatformLogo p={p} size="w-3.5 h-3.5 mt-0.5" />
                    <span className="line-clamp-2">
                      {q.latest[p].snippet || <span className="text-gray-400">Cevapta markanın adı geçmedi.</span>}
                      {q.latest[p].snippet && (q.latest[p].tone
                        ? <span className={TONE_TEXT[q.latest[p].tone]}> · {TONE_LABEL[q.latest[p].tone]}</span>
                        : q.latest[p].sentiment != null && <span className="text-gray-400"> · {SENT_LABEL[q.latest[p].sentiment]}</span>)}
                    </span>
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function Methodology({ data }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-6 py-5 text-left">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gray-100 text-gray-600"><BookOpen className="w-5 h-5" /></div>
          <div>
            <div className="text-lg font-semibold text-gray-800">Yöntem</div>
            <div className="text-sm text-gray-500">Ölçüm nasıl yapılıyor, skorlar nasıl hesaplanıyor, hata payı neyi ifade ediyor</div>
          </div>
        </div>
        <ChevronDown size={18} className={`text-gray-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 text-sm text-gray-700 leading-relaxed">
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Ölçüm</h3>
            <p>Sorular her ölçüm gününde otomatik olarak {platformNames(data)} platformlarına API üzerinden, oturumsuz ve kişiselleştirmesiz soruluyor. Sorularda marka adı geçmiyor. Modele ek talimat verilmiyor; tek ek bilgi kullanıcı konumu (İstanbul, Türkiye). Tüketici uygulaması da konumu modele bu şekilde iletiyor. Cevabın tamamı, kullandığı kaynaklar ve yaptığı web aramaları saklanıyor.</p>
            <p className="mt-2">Web araması her platformda açık, ama aramanın yapılıp yapılmayacağına modelin kendisi karar veriyor. Her cevapta arama yapılıp yapılmadığı ve arama sorguları kaydediliyor.</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Markaların ayrıştırılması</h3>
            <p>Her cevap ayrı bir dil modeliyle okunuyor. Cevapta geçen her marka için şu bilgiler çıkarılıyor: ilk tercih mi, önerildi mi, sadece adı mı geçti, karşılaştırıldı mı, olumsuz mu anıldı; öneri sırası; öneri gerekçesi (ihtiyaç kodları) ve atıf yapılan kaynaklar. Takip edilen markalar alias sözlüğüyle eşleştiriliyor. Sözlükte olmayan markalar da kaydediliyor; yazım farkları (ör. "Sui Sleep" / "Suisleep") tek marka olarak birleştiriliyor.</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Metrikler</h3>
            <ul className="list-disc pl-4 space-y-1">
              <li><b>Share of Recommendation:</b> markanın aldığı öneri ÷ dönemde verilen tüm öneriler. Sadece "ilk tercih" ve "önerildi" sayılıyor, adının geçmesi sayılmıyor. Paydaya takip dışı markalar da dahil.</li>
              <li><b>Geçme / Önerilme / İlk 3:</b> markanın adının geçtiği, önerildiği ve ilk üç öneri içinde yer aldığı cevapların oranı.</li>
              <li><b>Ortalama sıra:</b> önerildiği cevaplardaki ortalama öneri sırası.</li>
              <li>Sıralama Share of Recommendation'ı ağırlıklandırmıyor. İlk 3 ve ortalama sıra ayrı gösteriliyor.</li>
              {data?.worlds?.length > 0 && <li><b>Segmentler:</b> Uyku Dünyası (Yataş Bedding, Puffy) genel uyku, yatak, baza-başlık, yastık-yorgan ve ev tekstili sorularıyla; Mobilya Dünyası (Enza Home, Divanev) genel mobilya, koltuk, yemek odası, yatak odası, bahçe ve ev dekoru sorularıyla, her biri kendi rakip listesiyle ölçülüyor. Sorular Yataş'ın verdiği listeden. Öneri payının paydası yine o sorulardaki bütün öneriler.</li>}
              {data?.hasSearchSet && <li><b>Arama verisinden sorular:</b> Yataş Bedding'in Google Ads'te gerçekten aranan, ama soru listesinde karşılığı olmayan konularından (ölçüye göre yatak, battaniye, reflü yastığı gibi) türetilen sorular. Marka adı içermez. Liste rakamlarına karışmasın diye varsayılan görünüme girmez; filtreden "Arama verisinden" seçilince ayrı ölçülür.</li>}
              {data?.worlds?.length > 0 && <li><b>Marka adı geçen sorular</b> ("Enza Home kaliteli mi?" gibi) hiçbir oranın paydasına girmiyor; ayrı bölümde gösteriliyor.</li>}
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Dönem, hata payı ve kıyas</h3>
            <p>Seçilen dönemdeki tüm ölçümlerin cevapları tek havuzda toplanıyor ve her cevap eşit ağırlıkta sayılıyor. Aylık skor, o ayın bütün ölçümlerinden oluşuyor.</p>
            <p className="mt-2"><b>%95 aralık</b> soru bazında bootstrap yöntemiyle hesaplanıyor. Sorular 1.000 kez yeniden örnekleniyor; her örnekte bir sorunun o dönemdeki tüm cevapları birlikte alınıyor. Bu, modelin aynı soruya farklı cevaplar vermesinden kaynaklanan oynaklığı da içeriyor.</p>
            <p className="mt-2"><b>Kıyasta</b> yalnızca iki dönemde de sorulan sorular kullanılıyor. Farkın %95 aralığı sıfırı içermiyorsa değişim renkli okla "anlamlı" olarak işaretleniyor. Sıfırı içeriyorsa "≈" gösteriliyor, yani fark doğal değişkenlik sınırında.</p>
            <p className="mt-2"><b>Tekrar tutarlılığı:</b> aynı soru aynı platformda birden fazla kez cevaplandığında, önerilen marka listelerinin ne kadar örtüştüğünü gösteriyor (Jaccard: 1 = hep aynı markalar, 0 = hiç ortak marka yok).</p>
          </div>
          <div className="lg:col-span-2 text-xs text-gray-500 border-t border-gray-100 pt-3">
            Sınırlar: Ölçümler API üzerinden yapılıyor. Tüketici uygulamaları farklı bir varsayılan model, hafıza ve kişiselleştirme kullanabiliyor; bu yüzden sonuçlar tüketicinin gördüğüne yakın ama onunla birebir aynı değil. Model sürümü değişirse dönem künyesinde görünüyor, kıyasta da uyarı çıkıyor. Kaynak–öneri ilişkisi birlikte geçmeye dayanıyor, nedensellik iddiası taşımıyor.
            {data?.models?.length > 0 && <> Bu dönemde kullanılan modeller: {data.models.map((m) => `${PLATFORM_LABEL[m.platform] || m.platform} ${m.version} (${m.n} cevap)`).join(', ')}.</>}
          </div>
        </div>
      )}
    </div>
  );
}

// publicSlug: giriş gerektirmeyen paylaşım linki (pages/public/YatasAiVisibilityPublic).
// O modda veri /public/<slug>/ uçlarından, oturumsuz ve marka başlığı olmadan gelir;
// proje/marka backend'de slug'dan çözülür.
export default function YatasAiVisibility({ publicSlug = null, publicToken = null, onAuthError = null }) {
  const { selectedBrand, user } = useAuth();
  const brandId = publicSlug ? null : selectedBrand?.id;
  const allowed = !!publicSlug || hasAiVisibility(brandId, user?.email);
  const apiBase = publicSlug ? `${API}/api/ai-visibility/public/${publicSlug}` : `${API}/api/ai-visibility`;
  // Paylaşım linki: kendi kullanıcı adı/şifresiyle alınan anahtar (401 → giriş formuna dön)
  const fetchOpts = publicSlug ? { headers: { Authorization: `Bearer ${publicToken}` } } : { credentials: 'include', headers: { 'X-Brand-ID': brandId } };

  const defaultPeriod = () => { const p = presetRanges()[3]; return { from: p.from, to: p.to }; }; // Bu ay
  const [period, setPeriod] = useState(defaultPeriod); // {from,to} | {runId}
  const [cmp, setCmp] = useState(null); // {from,to} | null
  const [platform, setPlatform] = useState(null);
  const [category, setCategory] = useState(null);
  const [world, setWorld] = useState(null); // 'uyku' | 'mobilya'; null = backend panelde seçili markanın dünyasını seçer
  // Soru seti: null = müşterinin listesi (backend varsayılanı); 'arama' = Google Ads arama
  // terimlerinden türetilen sorular (ST-*); 'tumu' = hepsi. Yalnız data.hasSearchSet ise görünür.
  const [qset, setQset] = useState(null);
  // Aynı markanın birden çok projesi olabilir (glowy: derma-tr + kozmetik-tr). null = backend'in ilk projesi
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showAllUnknown, setShowAllUnknown] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawer, setDrawer] = useState({ open: false, title: '', loading: false, responses: [] });
  const [promptFilter, setPromptFilter] = useState('');

  const selQs = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    const all = {
      ...(period?.runId ? { runId: period.runId } : { from: period?.from, to: period?.to }),
      platform, category, world, set: qset, project, ...extra,
    };
    Object.entries(all).forEach(([k, v]) => { if (v != null && v !== '') p.set(k, v); });
    return p;
  }, [period, platform, category, world, qset, project]);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true); setError(null);
    try {
      const p = selQs();
      if (cmp?.from) { p.set('cmpFrom', cmp.from); p.set('cmpTo', cmp.to); }
      const res = await fetch(`${apiBase}/overview?${p}`, fetchOpts);
      if (res.status === 401 && publicSlug) { onAuthError?.(); return; }
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setData(j);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [allowed, apiBase, publicSlug, publicToken, brandId, selQs, cmp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Marka değişince (başka proje) seçimi sıfırla — eski ölçüm yeni projede yok
  useEffect(() => { setPeriod(defaultPeriod()); setCmp(null); setCategory(null); setWorld(null); setProject(null); }, [brandId]);
  useEffect(() => {
    setProjects([]);
    if (publicSlug || !allowed || !brandId) return;
    fetch(`${apiBase}/projects`, fetchOpts).then((r) => (r.ok ? r.json() : { projects: [] }))
      .then((j) => setProjects(j.projects || [])).catch(() => setProjects([]));
  }, [brandId, allowed, publicSlug]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeProject = (slug) => { setProject(slug); setWorld(null); setCategory(null); setCmp(null); setPeriod(defaultPeriod()); };
  useEffect(() => { load(); }, [load]);

  const sel = data?.selection;
  const openResponses = async (title, extra) => {
    setDrawer({ open: true, title, loading: true, responses: [] });
    try {
      const p = selQs(sel?.mode === 'run' ? { runId: sel.runIds[0] } : { from: sel?.from, to: sel?.to });
      Object.entries(extra).forEach(([k, v]) => p.set(k, v));
      const res = await fetch(`${apiBase}/responses?${p}`, fetchOpts);
      if (res.status === 401 && publicSlug) { onAuthError?.(); return; }
      const j = await res.json();
      setDrawer({ open: true, title, loading: false, responses: j.responses || [] });
    } catch (e) { setDrawer({ open: true, title, loading: false, responses: [] }); }
  };

  const toggleCompare = () => {
    if (cmp) { setCmp(null); return; }
    if (sel?.from) setCmp(prevRange({ from: sel.from, to: sel.to }));
  };

  const own = useMemo(() => (data?.brandTable || []).filter((b) => b.kind === 'own'), [data]);
  const platforms = useMemo(() => PLATFORM_ORDER.filter((p) => (data?.totals?.platforms || []).includes(p)), [data]);
  const availablePlatforms = useMemo(() => {
    const set = new Set();
    for (const r of data?.runs || []) for (const e of r.config?.platforms || []) set.add(e.platform);
    return PLATFORM_ORDER.filter((p) => set.has(p));
  }, [data]);
  // Isı haritası sütunları: kendi markalarımız + en çok öneri payı alan 6 rakip
  const matrixBrands = useMemo(() => {
    const t = data?.brandTable || [];
    return [...t.filter((b) => b.kind === 'own'), ...t.filter((b) => b.kind !== 'own').slice(0, data?.set ? 9 : (t.some((b) => b.kind === 'own') ? 6 : 10))];
  }, [data]);
  const maxShare = Math.max(0.0001, ...(data?.brandTable || []).map((b) => b.ci?.share?.[1] || b.share || 0));
  const totalCitations = (data?.sourceTypes || []).reduce((s, x) => s + x.n, 0);
  const comparison = data?.comparison && !data.comparison.empty ? data.comparison : null;
  const cmpOf = (id) => comparison?.brands?.[id] || null;

  if (!allowed) return <AiVisibilityLanding />;

  const headerPlatforms = availablePlatforms.length ? availablePlatforms : ['openai', 'gemini', 'claude'];

  return (
    <div className="min-h-screen" lang="tr">
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Başlık */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">AI Visibility</h1>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              {platformNames(data) || 'Yapay zeka asistanları'} platformlarına içinde marka adı geçmeyen gerçek tüketici soruları soruluyor. Bu sayfa hangi markanın ne sıklıkla, hangi ihtiyaçta ve hangi kaynağa dayanarak önerildiğini gösteriyor.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {headerPlatforms.map((p) => (
              <span key={p} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-white border border-gray-200/80 shadow-sm text-sm font-medium text-gray-800">
                <PlatformLogo p={p} />{PLATFORM_LABEL[p]}
              </span>
            ))}
          </div>
        </div>

        {/* Filtreler */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm px-4 py-3 flex flex-wrap items-center gap-2">
          {projects.length > 1 && (
            <select value={project || data?.project?.slug || ''} onChange={(e) => changeProject(e.target.value)}
              className="text-sm font-semibold border border-gray-300 rounded-xl pl-3 pr-8 py-2 bg-gray-900 text-white shadow-sm">
              {projects.map((pr) => <option key={pr.slug} value={pr.slug}>{pr.name.replace(/ · AI Visibility$/, '')}</option>)}
            </select>
          )}
          {(data?.worlds || []).length > 0 && (
            <>
              <div className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 pl-3 pr-1 py-1">
                <Layers className="w-4 h-4 text-blue-600" />
                <select value={data.world || ''} onChange={(e) => { setWorld(e.target.value); setCategory(null); }}
                  className="text-sm font-semibold text-blue-800 bg-transparent border-0 focus:ring-0 pr-8 py-1">
                  {data.worlds.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
                </select>
              </div>
              <select value={category || ''} onChange={(e) => setCategory(e.target.value || null)}
                className="text-sm border border-gray-200/80 rounded-xl pl-3 pr-8 py-2 bg-white shadow-sm font-medium text-gray-700">
                <option value="">Tüm kategoriler</option>
                {(data?.categories || []).map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
              </select>
              {data?.hasSearchSet && (
                <select value={qset || 'liste'} onChange={(e) => setQset(e.target.value === 'liste' ? null : e.target.value)}
                  title="Arama verisinden: Google Ads'te gerçekten aranan, markanın listesinde olmayan konulardan türetilen sorular"
                  className="text-sm border border-gray-200/80 rounded-xl pl-3 pr-8 py-2 bg-white shadow-sm font-medium text-gray-700">
                  <option value="liste">Soru listesi</option>
                  <option value="arama">Arama verisinden</option>
                  <option value="tumu">Tüm sorular</option>
                </select>
              )}
              <div className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
            </>
          )}
          <CalendarDays className="w-4 h-4 text-gray-400" />
          <PeriodPicker value={period?.from ? period : (sel?.from ? { from: sel.from, to: sel.to } : null)}
            onChange={(v) => setPeriod(v)} />
          {sel?.mode !== 'run' && (
            <button onClick={toggleCompare}
              className={`text-sm font-medium px-3 py-2 rounded-xl border shadow-sm ${cmp ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200/80 bg-white text-gray-700 hover:bg-gray-50'}`}>
              {cmp ? 'Kıyası kapat' : 'Önceki dönemle kıyasla'}
            </button>
          )}
          {cmp && <PeriodPicker value={cmp} onChange={(v) => setCmp(v)} label="kıyas:" />}
          <div className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
          <Seg value={platform} onChange={setPlatform}
            options={[{ value: null, label: 'Tümü' }, ...availablePlatforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))]} />
          {!(data?.worlds || []).length && (
            <select value={category || ''} onChange={(e) => setCategory(e.target.value || null)}
              className="text-sm border border-gray-200/80 rounded-xl pl-3 pr-8 py-2 bg-white shadow-sm font-medium text-gray-700">
              <option value="">Tüm kategoriler</option>
              {(data?.categories || []).map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
            </select>
          )}
          {loading && data && <Loader2 className="animate-spin text-gray-400 ml-auto" size={16} />}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl p-3">{error}</div>}
        {loading && !data && (
          <div className="flex justify-center items-center h-[240px]">
            <div className="loading-logo-container"><img src="/logoicon.png" alt="Glowytics" className="loading-logo" /></div>
          </div>
        )}
        {data?.empty && <Card><div className="text-sm text-gray-500">Seçilen dönemde ölçüm yok.</div></Card>}

        {data && !data.empty && data.totals && (
          <>
            {data.running && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-2xl p-3">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div>Seçili dönemdeki bir ölçüm hâlâ sürüyor. Sayılar tamamlanmamış veriye dayanıyor; cevaplar ayrıştırıldıkça tablolar dolacak.</div>
              </div>
            )}

            {/* Ölçümün kapsamı: ne soruldu, yapay zekalar nasıl cevap verdi */}
            <div>
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-gray-800">Ölçümün kapsamı</h2>
                <p className="text-sm text-gray-500">Seçilen dönemde yapay zekalara kaç soru soruldu ve cevaplarını nasıl oluşturdular.</p>
              </div>
              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${data.totals.perPlatform.length >= 3 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
                <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-5">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                    <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600"><Radar className="w-4 h-4" /></div>
                    {rangeLabel(sel)}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {[['Farklı soru', data.totals.prompts], ['Alınan cevap', data.totals.responses], ['Ölçüm günü', data.totals.runs]].map(([l, v]) => (
                      <div key={l}>
                        <div className="text-2xl font-bold text-gray-900">{Number(v).toLocaleString('tr-TR')}</div>
                        <div className="text-xs text-gray-500">{l}</div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                    Sorular haftada iki gün (Pazartesi ve Perşembe) İstanbul'dan, giriş yapmamış yeni bir kullanıcı gibi soruluyor. Böylece sonuç kişisel geçmişten etkilenmiyor.
                  </p>
                </div>
                {PLATFORM_ORDER.map((p) => data.totals.perPlatform.find((x) => x.platform === p)).filter(Boolean).map((x) => {
                  const searchRate = x.responses ? x.searched / x.responses : 0;
                  const cons = data.consistency?.[x.platform];
                  const consWord = !cons || cons.jaccard == null ? null : cons.jaccard >= 0.6 ? 'istikrarlı' : cons.jaccard >= 0.4 ? 'orta' : 'değişken';
                  return (
                    <div key={x.platform} className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-5">
                      <div className="flex items-center justify-between">
                        <PlatformName p={x.platform} className="text-sm font-medium text-gray-800" />
                        <span className="text-xs text-gray-500">{x.responses} cevap</span>
                      </div>
                      <div className="mt-4 flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-gray-900">{pct(searchRate)}</span>
                        <span className="text-xs text-gray-500">internette araştırarak cevapladı</span>
                      </div>
                      <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full rounded-full bg-blue-500" style={{ width: `${searchRate * 100}%` }} />
                      </div>
                      <p className="mt-2 text-[11px] text-gray-500">
                        {searchRate >= 0.99 ? 'Her cevapta güncel web sayfalarına baktı.' : 'Kalan cevapları kendi bilgisinden verdi (web sayfası açmadan).'}
                      </p>
                      {cons && consWord && (
                        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-gray-500"
                          title="Aynı soru aynı yapay zekaya farklı günlerde sorulduğunda önerilen marka listelerinin ne kadar örtüştüğü">
                          <Repeat className="w-3 h-3 mt-0.5 shrink-0" />
                          <span>Aynı soru tekrar sorulunca önerdiği markaların <b className="text-gray-700">{pct(cons.jaccard)}</b> kadarı aynı kalıyor ({consWord}).</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {(comparison || data.comparison?.empty) && (
              <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm px-4 py-3 text-xs text-gray-600">
                {comparison && (
                  <>
                    Kıyas: <b>{rangeLabel(comparison)}</b> ({comparison.runs.length} ölçüm, {comparison.totals.responses} cevap) · {comparison.commonPrompts} ortak soru üzerinden.
                    {comparison.modelChanged && <span className="text-amber-700"> Model sürümü iki dönem arasında farklı ({comparison.prevModels.join(', ')} → {comparison.curModels.join(', ')}).</span>}
                    {comparison.platformsDiffer && <span className="text-amber-700"> İki dönemdeki platformlar farklı; platform filtresiyle kıyaslamanız önerilir.</span>}
                  </>
                )}
                {data.comparison?.empty && <span className="text-amber-700">Kıyas döneminde ({rangeLabel(data.comparison)}) ölçüm yok.</span>}
              </div>
            )}

            {/* Dünya görünümü: iki markamız + rakipler arasındaki yerimiz */}
            {data.world && own.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {own.map((b) => <OwnBrandCard key={b.id} b={b} platforms={platform ? [] : platforms} cmp={cmpOf(b.id)} />)}
                <WorldRanking data={data} ownIds={own.map((b) => b.id)} className={own.length === 1 ? 'lg:col-span-2' : ''} />
              </div>
            )}

            {/* Kendi markalarımız */}
            {!data.world && own.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {own.map((b) => <OwnBrandCard key={b.id} b={b} platforms={platform ? [] : platforms} cmp={cmpOf(b.id)} />)}
              </div>
            )}

            <ShareTrend trend={data.trend} />

            {/* Rakip kıyası */}
            <section className="bg-white rounded-[32px] ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_64px_-28px_rgba(0,0,0,0.18)] overflow-hidden">
              <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-6 items-end">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">Marka karşılaştırması</div>
                  <h2 className="mt-1.5 text-[28px] sm:text-[34px] leading-[1.1] font-semibold tracking-[-0.03em] text-[#1d1d1f]">Yapay zekanın önerdiği markalar.</h2>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#6e6e73] max-w-3xl">Bu sorulara cevap verirken hangi markaları ne sıklıkla önerdi. Öneri payı, her 100 marka önerisinden kaçının o markaya gittiğini gösterir. Bir markaya tıklayınca geçtiği sorular ve cevaplar açılır.</p>
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {[[data.brandTable.length, 'marka'], [data.totals.responses, 'cevap'], [data.totals.allRecommendations, 'marka önerisi']].map(([v, l]) => (
                    <div key={l} className="rounded-2xl bg-[#f5f5f7] px-4 py-3 min-w-[92px]">
                      <div className="text-[22px] sm:text-[26px] leading-none font-semibold tracking-[-0.02em] text-[#1d1d1f] tabular-nums">{Number(v || 0).toLocaleString('tr-TR')}</div>
                      <div className="mt-1 text-[11px] text-[#86868b]">{l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* İlk üç */}
              {data.brandTable.length >= 3 && (
                <div className="px-6 sm:px-10 pb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {data.brandTable.slice(0, 3).map((b, i) => (
                    <button key={b.id} onClick={() => openResponses(b.name, { brandId: b.id })}
                      className={`text-left rounded-[24px] p-5 sm:p-6 transition ${b.kind === 'own' ? 'bg-[#0071e3]/[0.06] hover:bg-[#0071e3]/[0.1]' : 'bg-[#f5f5f7] hover:bg-[#ededf0]'}`}>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">{i + 1}. sıra</div>
                      <div className="mt-3 h-10 flex items-center"><AiBrandLogo name={b.name} size={30} maxW={190} fallback={<span className="text-[24px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{b.name}</span>} /></div>
                      <div className="mt-4 flex items-end justify-between gap-3">
                        <div>
                          <div className="text-[40px] leading-none font-semibold tracking-[-0.04em] text-[#1d1d1f] tabular-nums">{pct(b.share, 1)}</div>
                          <div className="mt-1.5 text-[12px] text-[#86868b]">öneri payı{b.ci?.share ? ` · ${pct(b.ci.share[0], 1)}–${pct(b.ci.share[1], 1)}` : ''}</div>
                        </div>
                        <div className="text-right text-[12px] leading-relaxed text-[#6e6e73] tabular-nums">
                          <div>Önerilme <b className="font-semibold text-[#1d1d1f]">{pct(b.recommendationRate)}</b></div>
                          <div>İlk 3 <b className="font-semibold text-[#1d1d1f]">{pct(b.top3Rate)}</b></div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="px-6 sm:px-10 pb-8">
                <div className="overflow-x-auto rounded-[20px] ring-1 ring-black/[0.06]">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#86868b] border-b border-black/[0.06]">
                        <th className="py-3 px-4 font-semibold w-10 text-right">#</th>
                        <th className="py-3 px-4 font-semibold">Marka</th>
                        <th className="py-3 px-4 font-semibold w-64 min-w-[180px]">Öneri payı</th>
                        {comparison && <th className="py-3 px-4 font-semibold text-right">Değişim</th>}
                        <th className="py-3 px-4 font-semibold text-right whitespace-nowrap">Metinde geçme</th>
                        <th className="py-3 px-4 font-semibold text-right">Önerilme</th>
                        <th className="py-3 px-4 font-semibold text-right whitespace-nowrap">İlk 3</th>
                        <th className="py-3 px-4 font-semibold text-right whitespace-nowrap">Ort. sıra</th>
                        {!platform && platforms.map((p) => (
                          <th key={p} className="py-3 px-4 font-semibold text-right normal-case tracking-normal text-[12px]" title={`${PLATFORM_LABEL[p]}'ta önerilme oranı`}>
                            <span className="inline-flex items-center gap-1.5 justify-end whitespace-nowrap"><PlatformLogo p={p} size="w-3.5 h-3.5" />{PLATFORM_LABEL[p]}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-black/[0.05]">
                      {data.brandTable.map((b, i) => (
                        <tr key={b.id} onClick={() => openResponses(b.name, { brandId: b.id })}
                          className={`group cursor-pointer transition ${b.kind === 'own' ? 'bg-[#0071e3]/[0.04] hover:bg-[#0071e3]/[0.08]' : 'hover:bg-[#f5f5f7]'}`}>
                          <td className="py-3 px-4 text-right text-[12px] text-[#86868b] tabular-nums">{i + 1}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center min-h-[24px] whitespace-nowrap">
                              <AiBrandLogo name={b.name} size={20} maxW={130} fallback={<span className={`text-[14px] font-semibold ${b.kind === 'own' ? 'text-[#0066cc]' : 'text-[#1d1d1f]'}`}>{b.name}</span>} />
                            </div>
                            <div className="mt-0.5 text-[11px] text-[#0066cc] inline-flex items-center gap-0.5 whitespace-nowrap opacity-0 group-hover:opacity-100 transition">Soruları gör <ChevronRight className="w-3 h-3" /></div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-1.5 rounded-full bg-black/[0.06] relative">
                                {b.ci && <div className={`absolute h-1.5 rounded-full ${b.kind === 'own' ? 'bg-[#0071e3]/25' : 'bg-[#1d1d1f]/15'}`}
                                  style={{ left: `${(b.ci.share[0] / maxShare) * 100}%`, width: `${((b.ci.share[1] - b.ci.share[0]) / maxShare) * 100}%` }} />}
                                <div className={`absolute -top-[3px] h-3 w-[3px] rounded-full ${b.kind === 'own' ? 'bg-[#0071e3]' : 'bg-[#1d1d1f]'}`} style={{ left: `calc(${((b.share || 0) / maxShare) * 100}% - 1.5px)` }} />
                              </div>
                              <div className="w-24 text-right leading-tight">
                                <div className="text-[14px] font-semibold text-[#1d1d1f] tabular-nums">{pct(b.share, 1)}</div>
                                <CiText ci={b.ci?.share} />
                              </div>
                            </div>
                          </td>
                          {comparison && <td className="py-3 px-4 text-right"><Delta c={cmpOf(b.id)?.share} /></td>}
                          <td className="py-3 px-4 text-right text-[#424245] tabular-nums">{pct(b.mentionRate)}</td>
                          <td className="py-3 px-4 text-right text-[#424245] tabular-nums">{pct(b.recommendationRate)}</td>
                          <td className="py-3 px-4 text-right text-[#424245] tabular-nums">{pct(b.top3Rate)}</td>
                          <td className="py-3 px-4 text-right text-[#424245] tabular-nums">{fmtRank(b.avgRank)}</td>
                          {!platform && platforms.map((p) => <td key={p} className="py-3 px-4 text-right text-[#6e6e73] tabular-nums">{pct(b.byPlatform[p]?.recommendationRate)}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.totals.allRecommendations > 0 && (
                  <div className="mt-5 space-y-1.5 text-[12px] leading-relaxed text-[#86868b] max-w-4xl">
                    <p><b className="font-semibold text-[#6e6e73]">Öneri payı:</b> her 100 öneriden kaçı bu markaya gitti; çizginin etrafındaki açık alan sonucun soru setine göre oynayabileceği aralık. <b className="font-semibold text-[#6e6e73]">Metinde geçme:</b> cevapların yüzde kaçında adı geçti. <b className="font-semibold text-[#6e6e73]">Önerilme:</b> yüzde kaçında önerildi. <b className="font-semibold text-[#6e6e73]">İlk 3:</b> yüzde kaçında ilk üç öneri arasındaydı. <b className="font-semibold text-[#6e6e73]">Ort. sıra:</b> önerildiğinde listede kaçıncı sıradaydı.</p>
                    <p>Bu dönemde yapılan {data.totals.allRecommendations.toLocaleString('tr-TR')} önerinin {data.totals.unknownRecommendations.toLocaleString('tr-TR')} tanesi rakip listesinde olmayan markalara gitti (aşağıda "Listede olmayan markalar").</p>
                  </div>
                )}
              </div>
              <GlowyFoot />
            </section>

            <BrandProducts data={data} onOpen={openResponses} />

            <ProductComparison data={data} platforms={platforms} platform={platform} onOpen={openResponses} />

            <ToneCard data={data} own={own} onOpen={openResponses} />

            {/* İhtiyaç matrisi */}
            {(() => {
              const rowsN = data.needMatrix || [];
              const allV = rowsN.flatMap((n) => matrixBrands.map((b) => n.rates[b.id] || 0)).filter((v) => v > 0).sort((x, y) => x - y);
              const vmax = Math.max(0.25, allV.length ? allV[Math.floor((allV.length - 1) * 0.9)] : 0.25); // %90'lık dilim: tek bir %100 ölçeği soldurmasın
              const cell = (v) => {
                if (!v) return { background: 'transparent', color: '#c7c7cc' };
                const t = Math.min(1, v / vmax);
                return { background: `rgba(0,113,227,${(0.06 + t * 0.84).toFixed(3)})`, color: t > 0.55 ? '#fff' : '#1d1d1f' };
              };
              return (
                <section className="bg-white rounded-[32px] ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_64px_-28px_rgba(0,0,0,0.18)] overflow-hidden">
                  <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-6 items-end">
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">Topic Ownership</div>
                      <h2 className="mt-1.5 text-[28px] sm:text-[34px] leading-[1.1] font-semibold tracking-[-0.03em] text-[#1d1d1f]">Hangi ihtiyaçta kim öneriliyor.</h2>
                      <p className="mt-3 text-[15px] leading-relaxed text-[#6e6e73] max-w-3xl">Satır, sorunun sorduğu ihtiyaç. Hücre, o sorulara verilen cevapların yüzde kaçında markanın önerildiği. Sağdaki sütun ihtiyacın sahibini gösterir.</p>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[#86868b]">
                      <span>Az</span>
                      <span className="h-2 w-40 rounded-full" style={{ background: 'linear-gradient(to right, rgba(0,113,227,0.06), rgba(0,113,227,0.9))' }} />
                      <span>{pct(vmax)} ve üstü</span>
                    </div>
                  </div>
                  <div className="px-6 sm:px-10 pb-8">
                    <div className="overflow-x-auto rounded-[20px] ring-1 ring-black/[0.06]">
                      <table className="w-full text-[13px] border-separate border-spacing-0">
                        <thead>
                          <tr>
                            <th className="sticky left-0 z-10 bg-white text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#86868b] py-3 px-4 border-b border-black/[0.06] min-w-[200px]">İhtiyaç</th>
                            {matrixBrands.map((b) => (
                              <th key={b.id} className="py-3 px-1.5 border-b border-black/[0.06] align-bottom">
                                <div className="h-7 flex items-end justify-center" title={b.name}>
                                  <AiBrandLogo name={b.name} size={14} maxW={68} fallback={<span className={`text-[11px] font-semibold leading-tight ${b.kind === 'own' ? 'text-[#0066cc]' : 'text-[#424245]'}`}>{b.name}</span>} />
                                </div>
                              </th>
                            ))}
                            <th className="py-3 px-4 border-b border-black/[0.06] text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#86868b] whitespace-nowrap">Sahibi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rowsN.map((n) => {
                            const lead = matrixBrands.reduce((acc, b) => ((n.rates[b.id] || 0) > (acc ? n.rates[acc.id] || 0 : 0) ? b : acc), null);
                            return (
                              <tr key={n.need} className="group">
                                <td className="sticky left-0 z-10 bg-white group-hover:bg-[#fbfbfd] py-2 px-4 border-b border-black/[0.04]">
                                  <div className="text-[14px] text-[#1d1d1f] whitespace-nowrap">{n.label}</div>
                                  <div className="text-[11px] text-[#86868b] tabular-nums">{n.responses} cevap</div>
                                </td>
                                {matrixBrands.map((b) => {
                                  const v = n.rates[b.id] || 0;
                                  return (
                                    <td key={b.id} className="p-[3px] border-b border-black/[0.04]">
                                      <div title={`${b.name} · ${n.label}: ${pct(v)}`} style={cell(v)}
                                        className={`rounded-[10px] text-center text-[12px] font-medium tabular-nums py-2 min-w-[58px] ${lead && lead.id === b.id && v ? 'ring-2 ring-[#1d1d1f]/80 ring-offset-1' : ''}`}>{v ? pct(v) : '–'}</div>
                                    </td>
                                  );
                                })}
                                <td className="py-2 px-4 border-b border-black/[0.04] whitespace-nowrap">
                                  {lead && (n.rates[lead.id] || 0) > 0 ? (
                                    <div className="flex items-center gap-2">
                                      <AiBrandLogo name={lead.name} size={16} maxW={90} fallback={<span className="text-[13px] font-semibold text-[#1d1d1f]">{lead.name}</span>} />
                                      <span className="text-[12px] font-semibold text-[#1d1d1f] tabular-nums">{pct(n.rates[lead.id])}</span>
                                    </div>
                                  ) : <span className="text-[12px] text-[#c7c7cc]">–</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-5 text-[12px] leading-relaxed text-[#86868b] max-w-4xl">Renk koyulaştıkça oran artar; çerçeveli hücre o ihtiyacın en çok önerilen markası. Tabloda en çok öneri payı alan markalar var, tamamı için yukarıdaki marka karşılaştırmasına bakın.</p>
                  </div>
                  <GlowyFoot />
                </section>
              );
            })()}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Neden öneriliyor */}
              <Card icon={Sparkles} tone="amber" title="Markalarımız neden öneriliyor"
                sub="Gerekçe: cevabın markayı önerirken öne çıkardığı ihtiyaç (kaç öneride geçti). Kaynak: markayı öneren cümlenin yanında atıf yapılan siteler.">
                {own.length === 0 && <div className="text-sm text-gray-500">Henüz veri yok.</div>}
                <div className="space-y-5">
                  {own.map((b) => (
                    <div key={b.id}>
                      <div className="text-sm font-semibold text-gray-800 mb-2">{b.name}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {(data.reasonsByBrand[b.id] || []).slice(0, 10).map((r) => (
                          <span key={r.code} className="inline-flex items-center gap-1.5 text-xs pl-2.5 pr-1 py-1 rounded-full bg-gray-50 border border-gray-200/80 text-gray-700">
                            {r.label}<b className="px-1.5 rounded-full bg-blue-100 text-blue-700 text-[11px]">{r.n}</b>
                          </span>
                        ))}
                        {!(data.reasonsByBrand[b.id] || []).length && <span className="text-xs text-gray-400">Gerekçeli öneri yok</span>}
                      </div>
                      {(data.sourcesByBrand?.[b.id] || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
                          <span className="text-gray-400">Kaynak:</span>
                          {data.sourcesByBrand[b.id].map((s) => (
                            <span key={s.domain} title={SOURCE_LABEL[s.type] || s.type}>{s.label || s.domain} <b className="text-gray-600">{s.n}</b></span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Kaynaklar — yalnız kaynak türü dağılımı (Yataş: 24 Eyl, madde 8) */}
              <Card icon={Globe} tone="green" title="Yapay zeka nereden bilgi alıyor"
                sub="Yapay zeka cevap verirken kaynak olarak gösterdiği linklerin hangi tür sitelerden geldiği. Bu siteler, yapay zekanın markalar hakkındaki görüşünü şekillendiren yerler.">
                <div className="space-y-2.5">
                  {(data.sourceTypes || []).map((s) => (
                    <div key={s.type} className="flex items-center gap-3 text-sm">
                      <span className="w-56 text-gray-700 truncate">{SOURCE_LABEL[s.type] || s.type}</span>
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${(s.n / Math.max(1, totalCitations)) * 100}%` }} /></div>
                      <span className="w-12 text-right font-medium text-gray-700">{pct(s.n / Math.max(1, totalCitations))}</span>
                    </div>
                  ))}
                </div>
                {totalCitations > 0 && (() => {
                  const top = data.sourceTypes[0];
                  const share = top.n / totalCitations;
                  return (
                    <div className="mt-5 rounded-2xl bg-gray-50 px-4 py-3 text-xs text-gray-600 leading-relaxed">
                      <b className="text-gray-800">Nasıl okunur:</b> Bu dönemde cevaplarda {totalCitations.toLocaleString('tr-TR')} kaynak linki verildi.
                      {' '}Bunların {pct(share)} kadarı "{SOURCE_LABEL[top.type] || top.type}" türünde; yani yapay zeka en çok bu tür sitelere bakarak cevap veriyor.
                      {' '}Markanın kendi sitesi, içerik siteleri ve perakendecilerdeki bilgilerin güncel ve doğru olması bu yüzden önemli.
                      {' '}Gemini çoğu cevabı internette aramadan verdiği için linklerin çoğu ChatGPT ve Claude'dan geliyor.
                    </div>
                  );
                })()}
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Sorular */}
              <div className="lg:col-span-2">
                <Card icon={MessageSquare} title="Sorular" sub="Yapay zekaya sorulan tüketici soruları. Bir soruya tıklayınca ChatGPT, Gemini ve Claude'un verdiği cevapları okuyabilirsiniz."
                  right={
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                      <input value={promptFilter} onChange={(e) => setPromptFilter(e.target.value)} placeholder="Ara"
                        className="pl-8 pr-2 py-1.5 text-sm border border-gray-200/80 rounded-xl shadow-sm w-40" />
                    </div>
                  }>
                  <div className="divide-y divide-gray-100">
                    {data.prompts
                      .filter((p) => !category || p.category === category)
                      .filter((p) => !promptFilter || p.text.toLocaleLowerCase('tr').includes(promptFilter.toLocaleLowerCase('tr')))
                      .map((p) => (
                        <button key={p.id} onClick={() => openResponses(p.text, { promptId: p.id })}
                          className="w-full text-left py-2.5 flex items-start gap-3 hover:bg-gray-50/80 rounded-lg px-2">
                          <span className="text-[11px] font-medium text-gray-400 w-16 shrink-0 pt-0.5">{p.key}</span>
                          <span className="flex-1 text-sm text-gray-800">{p.text}</span>
                          <span className="hidden sm:inline text-[11px] text-gray-500 shrink-0 pt-0.5 max-w-[220px] text-right">{CATEGORY_LABEL[p.category] || p.category}{p.needLabel ? ` · ${p.needLabel}` : ''}</span>
                          <span className="text-[11px] text-gray-400 w-14 text-right shrink-0 pt-0.5">{p.responses} cevap</span>
                        </button>
                      ))}
                  </div>
                </Card>
              </div>

              {/* Listede olmayan markalar — kısa etiket listesi (Yataş: 24 Eyl, madde 9) */}
              <Card icon={HelpCircle} tone="rose" title="Listede olmayan markalar"
                sub="Yapay zekanın önerdiği ama rakip listemizde olmayan markalar. Sık çıkanlar rakip listesine eklenebilir.">
                {(data.unknownBrands || []).length === 0 && <div className="text-sm text-gray-500">Yok.</div>}
                <div className="flex flex-wrap gap-1.5">
                  {(data.unknownBrands || []).slice(0, showAllUnknown ? undefined : 12).map((u) => (
                    <span key={u.name} title={`${u.recommended} öneri · ${u.responses} cevapta geçti${u.variants?.length ? ` · ayrıca: ${u.variants.join(', ')}` : ''}`}
                      className="inline-flex items-center gap-1.5 text-xs pl-2.5 pr-1 py-1 rounded-full bg-gray-50 border border-gray-200/80 text-gray-700">
                      {u.name}<b className="px-1.5 rounded-full bg-rose-100 text-rose-700 text-[11px]">{u.recommended}</b>
                    </span>
                  ))}
                </div>
                {(data.unknownBrands || []).length > 12 && (
                  <button onClick={() => setShowAllUnknown((v) => !v)} className="mt-3 text-xs font-medium text-blue-600 hover:underline">
                    {showAllUnknown ? 'Daha az göster' : `Tümünü göster (${data.unknownBrands.length})`}
                  </button>
                )}
                <div className="mt-3 text-[11px] text-gray-400">Sayı: bu dönemde kaç kez önerildiği.</div>
              </Card>
            </div>

            {data.world && <BrandedQuestions data={data} onOpen={openResponses} />}

            <Methodology data={data} />
          </>
        )}
      </div>

      <ResponsesDrawer {...drawer} onClose={() => setDrawer((d) => ({ ...d, open: false }))} />
    </div>
  );
}

// Kaynak × Marka sayfası (pages/AiVisibilitySources.js) aynı filtre dilini kullanıyor
export {
  API, AI_VISIBILITY_BRAND_IDS, PLATFORM_LABEL, PLATFORM_ORDER, PlatformLogo, CATEGORY_LABEL, SOURCE_LABEL,
  Card, Seg, PeriodPicker, presetRanges, rangeLabel, pct, GlowyFoot,
};
